import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Camera, Mail } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import API_URL from '../../config';
import { useUploadPhoto } from '../../hooks/usePhotos';
import './Profile.css';

// Generate a consistent color based on the name
function getAvatarColor(name) {
  const colors = [
    '#3498db', '#e74c3c', '#2ecc71', '#9b59b6', '#f39c12',
    '#1abc9c', '#e67e22', '#34495e', '#16a085', '#c0392b'
  ];
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  return colors[Math.abs(hash) % colors.length];
}

// Get initials from name
function getInitials(name) {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) {
    return parts[0].charAt(0).toUpperCase();
  }
  return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
}

function Avatar({ name, size = 'large', avatarUrl }) {
  const initials = getInitials(name);
  const color = getAvatarColor(name || '');
  const sizeClass = size === 'small' ? 'avatar-small' : 'avatar-large';

  if (avatarUrl) {
    return (
      <img
        src={avatarUrl}
        alt={name}
        className={`avatar ${sizeClass} avatar-img`}
      />
    );
  }

  return (
    <div
      className={`avatar ${sizeClass}`}
      style={{ backgroundColor: color }}
    >
      {initials}
    </div>
  );
}

function Profile() {
  const { user, getAuthHeader } = useAuth();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [family, setFamily] = useState('');
  const [avatarUrl, setAvatarUrl] = useState(null);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [showPasswordSection, setShowPasswordSection] = useState(false);
  const [isPublic, setIsPublic] = useState(false);
  const fileInputRef = useRef(null);
  const uploadPhoto = useUploadPhoto();

  useEffect(() => {
    if (user) {
      setName(user.name || '');
      setEmail(user.email || '');
    }
    fetchProfile();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const fetchProfile = async () => {
    try {
      const response = await fetch(`${API_URL}/api/users/profile`, {
        headers: getAuthHeader()
      });
      if (response.ok) {
        const data = await response.json();
        setName(data.name || '');
        setEmail(data.email || '');
        setFamily(data.family || '');
        setIsPublic(data.is_public || false);
        setAvatarUrl(data.avatar_url || null);
      }
    } catch (error) {
      console.error('Error fetching profile:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage({ type: '', text: '' });

    // Validate password confirmation
    if (newPassword && newPassword !== confirmPassword) {
      setMessage({ type: 'error', text: 'New passwords do not match' });
      return;
    }

    setLoading(true);

    try {
      const updateData = {};

      if (name && name !== user?.name) {
        updateData.name = name;
      }

      if (newPassword) {
        if (!currentPassword) {
          setMessage({ type: 'error', text: 'Current password is required to change password' });
          setLoading(false);
          return;
        }
        updateData.currentPassword = currentPassword;
        updateData.newPassword = newPassword;
      }

      // Always include visibility setting
      updateData.is_public = isPublic;

      if (Object.keys(updateData).length === 0) {
        setMessage({ type: 'error', text: 'No changes to save' });
        setLoading(false);
        return;
      }

      const response = await fetch(`${API_URL}/api/users/profile`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeader()
        },
        body: JSON.stringify(updateData)
      });

      const data = await response.json();

      if (response.ok) {
        setMessage({ type: 'success', text: 'Profile updated successfully' });
        setCurrentPassword('');
        setNewPassword('');
        setConfirmPassword('');
        setShowPasswordSection(false);
        // Refresh to get updated user data
        window.location.reload();
      } else {
        setMessage({ type: 'error', text: data.error || 'Failed to update profile' });
      }
    } catch (error) {
      console.error('Error updating profile:', error);
      setMessage({ type: 'error', text: 'Failed to update profile' });
    }

    setLoading(false);
  };

  const handleAvatarClick = () => {
    fileInputRef.current && fileInputRef.current.click();
  };

  const handleAvatarChange = async (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;

    const allowed = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
    if (!allowed.includes(file.type)) {
      setMessage({ type: 'error', text: 'Please choose a JPEG, PNG, WEBP, or GIF image.' });
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setMessage({ type: 'error', text: 'Image must be under 5MB.' });
      return;
    }

    setMessage({ type: '', text: '' });
    try {
      const result = await uploadPhoto.mutateAsync({ file, photoType: 'avatar' });
      setAvatarUrl(result.publicUrl);
      setMessage({ type: 'success', text: 'Profile picture updated.' });
    } catch (err) {
      setMessage({ type: 'error', text: 'Failed to upload photo. Please try again.' });
    }
    // Reset input so the same file can be re-selected if needed
    e.target.value = '';
  };

  return (
    <div className="profile-container">
      <div className="profile-box">
        <div className="profile-header">
          <div
            className="avatar-upload-wrapper"
            onClick={handleAvatarClick}
            title="Click to change profile picture"
          >
            <Avatar name={name} size="large" avatarUrl={avatarUrl} />
            <div className="avatar-upload-overlay">
              {uploadPhoto.isPending
                ? <span className="avatar-upload-spinner" />
                : <Camera size={20} />
              }
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              style={{ display: 'none' }}
              onChange={handleAvatarChange}
            />
          </div>
          <div className="profile-header-text">
            <h2>{name || 'My Profile'}</h2>
            <p>{email}</p>
          </div>
        </div>


        <form onSubmit={handleSubmit} className="profile-form">
          {message.text && (
            <div className={`message ${message.type}`}>
              {message.text}
            </div>
          )}

          <div className="form-section">
            <h3>Account Information</h3>

            <div className="form-group">
              <label htmlFor="email">Email</label>
              <input
                id="email"
                type="email"
                value={email}
                disabled
                className="disabled"
              />
              <span className="field-note">Email cannot be changed</span>
            </div>

            <div className="form-group">
              <label htmlFor="name">Display Name</label>
              <input
                id="name"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Your name"
                disabled={loading}
              />
            </div>

            {family && (
              <div className="form-group">
                <label htmlFor="family">Family</label>
                <input
                  id="family"
                  type="text"
                  value={family}
                  disabled
                  className="disabled"
                />
                <span className="field-note">Contact an admin to change family</span>
              </div>
            )}

            <div className="form-group visibility-group">
              <label>Profile Visibility</label>
              <div className="visibility-toggle-profile">
                <button
                  type="button"
                  className={`visibility-btn ${isPublic ? 'public' : 'private'}`}
                  onClick={() => setIsPublic(!isPublic)}
                  disabled={loading}
                >
                  {isPublic ? 'Public' : 'Private'}
                </button>
                <span className="field-note">
                  {isPublic
                    ? 'Other users can see your profile'
                    : 'Only admins can see your profile'}
                </span>
              </div>
            </div>
          </div>

          <div className="form-section password-section">
            <button
              type="button"
              className="section-toggle"
              onClick={() => setShowPasswordSection(!showPasswordSection)}
            >
              <span>Change Password</span>
              <span className={`toggle-icon ${showPasswordSection ? 'open' : ''}`}>
                {showPasswordSection ? '−' : '+'}
              </span>
            </button>

            {showPasswordSection && (
              <div className="password-fields">
                <div className="form-group">
                  <label htmlFor="currentPassword">Current Password</label>
                  <input
                    id="currentPassword"
                    type="password"
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    placeholder="Enter current password"
                    disabled={loading}
                    autoComplete="current-password"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="newPassword">New Password</label>
                  <input
                    id="newPassword"
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="At least 8 characters, with upper, lower and a number"
                    disabled={loading}
                    autoComplete="new-password"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="confirmPassword">Confirm New Password</label>
                  <input
                    id="confirmPassword"
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Confirm new password"
                    disabled={loading}
                    autoComplete="new-password"
                  />
                </div>
              </div>
            )}
          </div>

          <div className="form-section email-prefs-section">
            <h3>Email Preferences</h3>
            <p className="email-prefs-hint">
              Choose which emails you receive, including notices about open weekends.
            </p>
            <Link to="/preferences" className="email-prefs-link">
              <Mail size={15} aria-hidden="true" />
              <span>Manage email settings</span>
            </Link>
          </div>

          <div className="form-actions">
            <button
              type="submit"
              className="btn btn-primary"
              disabled={loading}
            >
              {loading ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// Export Avatar for use in other components (like navbar)
export { Avatar, getInitials, getAvatarColor };
export default Profile;
